import { BASE_PATH, type BuiltinAnswers } from '../../shared/types';
import type { PdfRect } from '../../shared/pdf/geometry';
import { builtinSourceLabel } from '../../shared/pdfBindingValidation';
import { ApiError } from './api';
import type { OrganizerPdfSource } from './pdfForms';
import { PERMISSION_FIELDS } from './permissionFields';

export const STANDARD_FORM_URL = `${BASE_PATH}/permission_medical_release.pdf`;

type FieldKey = Exclude<keyof typeof PERMISSION_FIELDS, 'reset'>;
const answer = (key: keyof BuiltinAnswers | 'participant') => builtinSourceLabel(key);
/**
 * What fills each of the standard form's fields, named as an organizer's own form names its answers where it can
 * (src/lib/pdf.ts fills them).
 */
const FIELD_NAMES: Record<FieldKey, string> = {
  event: 'Event name', dates: 'Activity dates', description: 'Event description', ward: 'Ward', stake: 'Stake name',
  leader: 'Organizer name', leaderPhone: 'Organizer phone number', leaderEmail: 'Organizer email',
  participant: answer('participant'), dob: answer('dateOfBirth'), age: 'Age on the first day of the event', phone: answer('telephone'),
  address: answer('address'), city: answer('city'), state: answer('stateOrProvince'),
  emergency: answer('emergencyContact'), emergencyPrimary: answer('emergencyPrimaryPhone'), emergencySecondary: answer('emergencySecondaryPhone'),
  specialDiet: answer('specialDiet'), dietExplanation: answer('dietExplanation'), allergies: answer('allergies'), allergyExplanation: answer('allergyExplanation'),
  medications: answer('medications'), selfAdmin: answer('selfAdminister'),
  chronicIllness: answer('chronicIllness'), chronicIllnessExplanation: answer('chronicIllnessExplanation'),
  surgery: answer('surgery'), surgeryExplanation: answer('surgeryExplanation'),
  otherLimitations: answer('otherLimitations'), specialNeeds: answer('specialNeeds'),
  participantSignature: 'Participant Signature', date1: 'Date signed', parentSignature: 'Parent or Guardian Signature', date2: 'Date signed',
};
// Fields that hold a date, shown as date fields like an organizer's own.
const DATES = new Set<FieldKey>(['dob', 'date1', 'date2']);
const NAMES = new Map(Object.entries(FIELD_NAMES).map(([key, name]) => [PERMISSION_FIELDS[key as FieldKey] as string, { key: key as FieldKey, name }]));

/** One of the standard form's fields: where it is and what fills it. A Yes/No box says which answer checks it. */
export interface StandardFormField { id: string; pageIndex: number; rect: PdfRect; kind: 'text' | 'date' | 'checkbox' | 'signature'; name: string }

/** The standard form's blank PDF and its fields, in reading order: down the page, then across. */
export async function loadStandardForm(signal: AbortSignal): Promise<{ source: OrganizerPdfSource; fields: StandardFormField[] }> {
  const response = await fetch(STANDARD_FORM_URL, { signal });
  if (!response.ok) throw new ApiError(response.status, 'template_unavailable');
  const bytes = new Uint8Array(await response.arrayBuffer());
  const [{ PDFCheckBox, PDFDict }, { loadPdfTemplate, inspectPageGeometry }] = await Promise.all([import('pdf-lib'), import('../../shared/pdf/inspect')]);
  const pdf = await loadPdfTemplate(bytes), pages = inspectPageGeometry(pdf);
  const pageOf = new Map<unknown, number>();
  pdf.getPages().forEach((page, index) => { const annots = page.node.Annots(); for (let i = 0; i < (annots?.size() ?? 0); i++) pageOf.set(pdf.context.lookup(annots!.get(i), PDFDict), index); });
  const fields: StandardFormField[] = pdf.getForm().getFields().flatMap(field => {
    const named = NAMES.get(field.getName());
    if (!named) return [];
    return field.acroField.getWidgets().map((widget, index) => {
      const on = field instanceof PDFCheckBox ? widget.getOnValue()?.decodeText() : undefined;
      return { id: `${named.key}-${index}`, pageIndex: pageOf.get(widget.dict) ?? 0, rect: widget.getRectangle(),
        kind: on ? 'checkbox' as const : named.key.endsWith('Signature') ? 'signature' as const : DATES.has(named.key) ? 'date' as const : 'text' as const, name: on ? `${named.name}${named.name.endsWith('?') ? '' : ':'} ${on}` : named.name };
    });
  }).sort((a, b) => {
    // Fields whose middles are within a few points share a row, such as a Yes/No pair and its explanation.
    const rise = (b.rect.y + b.rect.height / 2) - (a.rect.y + a.rect.height / 2);
    return a.pageIndex - b.pageIndex || (Math.abs(rise) > 6 ? rise : a.rect.x - b.rect.x);
  });
  // The completed form has no Reset button, so neither does this one.
  pdf.getForm().removeField(pdf.getForm().getButton(PERMISSION_FIELDS.reset));
  return { source: { bytes: await pdf.save(), pages, nativeFields: [] }, fields };
}
